import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useFetchQuantifyTableData } from '@modules/quantify/hooks/useFetchQuantifyTableData';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { ClearRounded, SearchRounded } from '@mui/icons-material';
import {
  IconButton,
  InputAdornment,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { GridToolbarQuickFilter } from '@mui/x-data-grid-pro';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { shallow } from 'zustand/shallow';

const trackQuantifyReportEvents = createAnalyticsTracker('quantifyReport');

export const QuantifyReportSearchBar = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchQuantifyTableData] = useFetchQuantifyTableData();
  const [shouldFetchTableData, setShouldFetchTableData] = useState(false);
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const shouldShowAllValues = useQuantifyStore(
    ({ metadata }) => metadata.shouldShowAllValues,
  );

  const { name, href, scope } = useAnalysisStore(
    (analysis) => ({
      name: analysis.title,
      href: analysis.href,
      scope: analysis.scope,
    }),
    shallow,
  );

  const onShouldShowAllValuesChange = (
    _: React.MouseEvent<HTMLElement>,
    newShouldShowAllValues: boolean,
  ) => {
    if (newShouldShowAllValues !== null) {
      dispatch({
        type: QuantifyActionType.SET_VISUAL,
        payload: {
          pageNumber: 1,
        },
      });

      dispatch({
        type: QuantifyActionType.SET_METADATA,
        payload: {
          shouldShowAllValues: newShouldShowAllValues,
        },
      });

      setShouldFetchTableData(true);

      trackQuantifyReportEvents({
        eventName: 'Quantify Report - Table View Toggled',
        shouldShowAllValues: newShouldShowAllValues,
        name,
        link: href,
        scope: mapFromEdgeScope(scope),
      });
    }
  };

  const debounceOnSearchTermChange = useMemo(
    () =>
      debounce((value: string) => {
        dispatch({
          type: QuantifyActionType.SET_VISUAL,
          payload: { searchTerm: value, pageNumber: 1 },
        });

        setShouldFetchTableData(true);
      }, 350),
    [dispatch],
  );

  const clearSearchTerm = () => {
    setSearchTerm('');
    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: { searchTerm: '', pageNumber: 1 },
    });
    setShouldFetchTableData(true);
  };

  useEffect(() => {
    if (shouldFetchTableData) {
      setShouldFetchTableData(false);
      fetchQuantifyTableData();
    }
  }, [shouldFetchTableData, fetchQuantifyTableData]);

  return (
    <Stack
      direction="row"
      spacing={2}
      mb={2}
      alignItems="flex-start"
      width="100%"
    >
      <GridToolbarQuickFilter
        fullWidth
        size="small"
        variant="outlined"
        sx={{
          width: '100%',
        }}
        value={searchTerm}
        onChange={(event) => {
          setSearchTerm(event.target.value);
          debounceOnSearchTermChange(event.target.value);
          trackQuantifyReportEvents({
            eventName: 'Quantify Report - Search',
            searchText: event.target.value,
            name,
            link: href,
            scope: mapFromEdgeScope(scope),
          });
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: !!searchTerm.length && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                color="primary"
                onClick={clearSearchTerm}
              >
                <ClearRounded />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <ToggleButtonGroup
        color="primary"
        value={shouldShowAllValues}
        exclusive
        onChange={onShouldShowAllValuesChange}
      >
        <ToggleButton
          sx={{ whiteSpace: 'nowrap' }}
          size="small"
          value={true}
          aria-label="all values"
        >
          All Values
        </ToggleButton>
        <ToggleButton
          sx={{ whiteSpace: 'nowrap' }}
          size="small"
          value={false}
          aria-label="selected"
        >
          Selected
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};
